import { ShadcnButton } from '@shadcn-features/core';
import { cx } from 'class-variance-authority';
import { Trash2Icon } from 'lucide-react';
import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@src/components/ui/typography';
import { FlowTabProps } from './FlowTabView';

export const FlowTabVerticalList: FC<FlowTabProps> = ({
    items,
    changeItem,
    deleteTab,
    childrenBtnForFlowTab,
    tabProps,
}) => {
    const { t } = useTranslation();

    const [selected, setSelected] = useState<string | null>(items?.[0]?.key);
    const [isCheckData, setIsCheckData] = useState(true);

    const sectionRef = useRef<HTMLDivElement | null>(null);

    const setSelectedItem = useMemo(() => {
        return items.find((item) => item.key === selected);
    }, [items, selected]);

    const selectedItemIndex = useMemo(() => {
        return items.findIndex((item) => item.key === selected);
    }, [items, selected]);

    const handleScroll = useCallback(() => {
        sectionRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    }, []);

    const handelChangeItem = useCallback(
        (selected: string) => {
            setIsCheckData(false);
            changeItem?.(selected);
            setSelected(selected);
            handleScroll();
        },
        [changeItem, handleScroll],
    );

    return (
        <div
            ref={sectionRef}
            className={cx(
                'flex w-full flex-col items-end gap-3',
                tabProps?.className,
            )}
        >
            <div className='flex w-full items-start gap-3.5'>
                <div className='flex min-w-0 flex-1 flex-col'>
                    {!isCheckData && (
                        <StepCard>
                            <ShowCurrentStepChild
                                changeItem={handelChangeItem}
                                deleteTab={deleteTab}
                                items={items}
                                selected={selected}
                                selectedItemIndex={selectedItemIndex}
                                setSelectedItem={setSelectedItem}
                            />
                        </StepCard>
                    )}

                    {isCheckData && (
                        <ShowPageCheckData
                            changeItem={handelChangeItem}
                            deleteTab={deleteTab}
                            items={items}
                            selected={selected}
                            selectedItemIndex={selectedItemIndex}
                            setSelectedItem={setSelectedItem}
                        />
                    )}

                    {isCheckData && childrenBtnForFlowTab && (
                        <div className='mt-6 flex w-full flex-col gap-2 bg-[var(--color-bg-v4)]'>
                            {childrenBtnForFlowTab}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

function StepCard({ children }: { children: React.ReactNode }) {
    return (
        <div className='w-full overflow-hidden rounded-xl p-5'>{children}</div>
    );
}

function ShowCurrentStepChild({
    selected,
    deleteTab,
    changeItem,
    setSelectedItem,
    selectedItemIndex,
    items,
    isReadonly,
}: {
    selected: string | null;
    deleteTab?: () => void;
    changeItem?: (id: string) => void;
    setSelectedItem?: FlowTabProps['items'][0];
    selectedItemIndex: number;
    items: FlowTabProps['items'];
    isReadonly?: boolean;
}) {
    const { t } = useTranslation();

    if (isReadonly) {
        return (
            <div className='flex flex-col'>
                <Typography.Title className='m-0 text-base font-bold' level={5}>
                    {setSelectedItem?.label}
                </Typography.Title>
                {setSelectedItem?.children && (
                    <div className='mt-5'>
                        {React.isValidElement(setSelectedItem.children)
                            ? React.cloneElement(setSelectedItem.children, {
                                  isReadOnly: true,
                                  classNameWrapper: 'gap-[0px]!',
                              })
                            : setSelectedItem.children}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className='w-full'>
            <div
                className='mb-[9px] flex items-center justify-between'
                hidden={isReadonly}
            >
                {selected && (
                    <span className='font-montserrat text-sm font-medium text-[var(--color-brand-green)]'>
                        {t('translation:step')} {selectedItemIndex + 1}
                    </span>
                )}
                {!selected && items.length === 0 && (
                    <ShadcnButton
                        className='text-[var(--color-danger)]!'
                        icon={<Trash2Icon size={14} />}
                        variant='icon'
                        popconfirm={{
                            title: t('translation:areYouSure'),
                            onConfirm: () => {
                                deleteTab?.();
                            },
                        }}
                    />
                )}
            </div>
            <span
                className='font-montserrat text-xl font-bold text-[var(--color-card-text)]'
                hidden={isReadonly}
            >
                {setSelectedItem?.label}
            </span>

            {setSelectedItem && (
                <div className='mt-[30px]'>{setSelectedItem.children}</div>
            )}
        </div>
    );
}

function ShowPageCheckData({
    ...props
}: Parameters<typeof ShowCurrentStepChild>[0]) {
    return (
        <div className='service-vertical-wrap flex w-full flex-col gap-3'>
            {props?.items?.map((item) => (
                <div
                    key={item.key}
                    className='service-vertical-item w-full rounded-xl'
                >
                    <ShowCurrentStepChild
                        {...props}
                        isReadonly
                        changeItem={props.changeItem}
                        setSelectedItem={item}
                    />
                </div>
            ))}
        </div>
    );
}
